import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box, Typography, Card, CardContent, Stack, Chip, IconButton, Button,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
  FormControl, InputLabel, Select, MenuItem, Snackbar, Alert,
  CircularProgress, Tooltip, TextField, Dialog, DialogTitle, DialogContent, DialogActions,
} from "@mui/material";
import GroupsIcon from "@mui/icons-material/Groups";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelIcon from "@mui/icons-material/Cancel";
import VisibilityIcon from "@mui/icons-material/Visibility";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import {
  fetchEvents, fetchRegistrations, updateRegistrationStatus, exportRegistrationsUrl,
  SpecialEvent, EventRegistration, RegistrationStatus, REG_STATUS_COLOR,
} from "./specialEventsApi";

const NAVY = "#0d1b2a";
const GOLD = "#c9a84c";

const EventRegistrationsList: React.FC = () => {
  const [events, setEvents] = useState<SpecialEvent[]>([]);
  const [rows, setRows]     = useState<EventRegistration[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [eventId, setEventId] = useState<string>("");
  const [status, setStatus]   = useState<string>("");
  const [search, setSearch]   = useState<string>("");

  // Detail dialog
  const [detail, setDetail] = useState<EventRegistration | null>(null);

  const [snack, setSnack] = useState<{ open: boolean; msg: string; severity: "success" | "error" | "info" }>({
    open: false, msg: "", severity: "success",
  });
  const showSnack = (msg: string, severity: "success" | "error" | "info" = "success") =>
    setSnack({ open: true, msg, severity });

  /* Build the query-string params common to fetchRegistrations and the
     export URL — so "what you see is what you export" stays a true
     invariant across both surfaces. */
  const queryParams = useMemo(() => {
    const p: Record<string, string> = {};
    if (eventId) p.event_id = eventId;
    if (status)  p.status   = status;
    if (search.trim()) p.search = search.trim();
    return p;
  }, [eventId, status, search]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [evs, regs] = await Promise.all([
        fetchEvents(),
        fetchRegistrations(queryParams),
      ]);
      setEvents(evs);
      setRows(regs);
    } catch {
      showSnack("Failed to load registrations", "error");
    }
    setLoading(false);
  }, [queryParams]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleStatusUpdate = async (id: number, newStatus: RegistrationStatus) => {
    try {
      await updateRegistrationStatus(id, newStatus);
      showSnack(`Marked ${newStatus}`);
      loadData();
    } catch (e: any) {
      showSnack(e.message ?? "Update failed", "error");
    }
  };

  const handleExport = () => {
    const url = exportRegistrationsUrl(queryParams);
    // Trigger the file download without leaving the page. The endpoint
    // is auth-protected so we can't just window.open(url) — it'd lose
    // the bearer token. Instead, fetch with the token, then build a
    // blob URL and click it. Standard SPA-download dance.
    fetch(url, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
    })
      .then((res) => res.blob())
      .then((blob) => {
        const a = document.createElement("a");
        const href = URL.createObjectURL(blob);
        a.href = href;
        a.download = `event-registrations-${Date.now()}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(href);
      })
      .catch(() => showSnack("Export failed", "error"));
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: "#f5f6fa", minHeight: "100vh" }}>
      {/* Header */}
      <Card elevation={0} sx={{
        mb: 2.5, borderRadius: 3,
        background: `linear-gradient(135deg, ${NAVY} 0%, #1e3a5f 100%)`, color: "#fff",
      }}>
        <CardContent sx={{ py: 2.5, px: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <Stack direction="row" alignItems="center" gap={2}>
              <GroupsIcon sx={{ fontSize: 38, opacity: 0.85 }} />
              <Box>
                <Typography variant="h5" fontWeight={800} lineHeight={1}>Event Registrations</Typography>
                <Typography variant="caption" sx={{ opacity: 0.75, mt: 0.5, display: "block" }}>
                  {rows.length} registration{rows.length === 1 ? "" : "s"} match current filters
                </Typography>
              </Box>
            </Stack>
            <Button
              variant="contained"
              startIcon={<FileDownloadIcon />}
              onClick={handleExport}
              sx={{ bgcolor: GOLD, color: NAVY, fontWeight: 700, borderRadius: 2, "&:hover": { bgcolor: "#b89540" } }}
            >
              Export XLSX
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* Filter bar */}
      <Card elevation={0} sx={{ mb: 2.5, borderRadius: 3, border: "1px solid #e8e8e8" }}>
        <CardContent sx={{ py: 2, px: 2 }}>
          <Stack direction={{ xs: "column", md: "row" }} gap={2}>
            <FormControl size="small" sx={{ minWidth: 240 }}>
              <InputLabel>Event</InputLabel>
              <Select
                value={eventId}
                onChange={(e) => setEventId(e.target.value as string)}
                label="Event"
              >
                <MenuItem value="">All events</MenuItem>
                {events.map((e) => (
                  <MenuItem key={e.id} value={String(e.id)}>{e.title}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={status}
                onChange={(e) => setStatus(e.target.value as string)}
                label="Status"
              >
                <MenuItem value="">All statuses</MenuItem>
                <MenuItem value="registered">Registered</MenuItem>
                <MenuItem value="attended">Attended</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
            <TextField
              size="small"
              placeholder="Search name / email / phone / organization"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              fullWidth
            />
          </Stack>
        </CardContent>
      </Card>

      {/* Table */}
      <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid #e8e8e8" }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#fafbfc" }}>
                <TableCell sx={{ fontWeight: 700, color: NAVY }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 700, color: NAVY }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 700, color: NAVY }}>Phone</TableCell>
                <TableCell sx={{ fontWeight: 700, color: NAVY }}>Organization</TableCell>
                <TableCell sx={{ fontWeight: 700, color: NAVY, textAlign: "center" }}>Guests</TableCell>
                <TableCell sx={{ fontWeight: 700, color: NAVY }}>Event</TableCell>
                <TableCell sx={{ fontWeight: 700, color: NAVY }}>Registered</TableCell>
                <TableCell sx={{ fontWeight: 700, color: NAVY }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, color: NAVY, textAlign: "right" }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell></TableRow>
              )}
              {!loading && rows.length === 0 && (
                <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  No registrations found.
                </TableCell></TableRow>
              )}
              {rows.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={700} color={NAVY}>{r.full_name}</Typography>
                  </TableCell>
                  <TableCell>{r.email ?? "—"}</TableCell>
                  <TableCell>{r.phone ?? "—"}</TableCell>
                  <TableCell>{r.organization ?? "—"}</TableCell>
                  <TableCell sx={{ textAlign: "center" }}>{r.guest_count ?? 1}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{r.event?.title ?? "—"}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {new Date(r.registered_at).toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={r.status}
                      size="small"
                      sx={{
                        height: 20, fontSize: 10, fontWeight: 700, textTransform: "capitalize",
                        bgcolor: `${REG_STATUS_COLOR[r.status]}18`,
                        color: REG_STATUS_COLOR[r.status],
                        border: `1px solid ${REG_STATUS_COLOR[r.status]}30`,
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" gap={0.3} justifyContent="flex-end">
                      <Tooltip title="View details">
                        <IconButton size="small" onClick={() => setDetail(r)}>
                          <VisibilityIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                      {r.status !== "attended" && (
                        <Tooltip title="Mark attended">
                          <IconButton size="small" onClick={() => handleStatusUpdate(r.id, "attended")} sx={{ color: "#16a34a" }}>
                            <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      {r.status !== "cancelled" && (
                        <Tooltip title="Cancel">
                          <IconButton size="small" onClick={() => handleStatusUpdate(r.id, "cancelled")} sx={{ color: "#ef4444" }}>
                            <CancelIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Detail dialog */}
      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: NAVY }}>Registration Details</DialogTitle>
        <DialogContent dividers>
          {detail && (
            <Stack gap={1.5}>
              <DetailRow label="Event" value={detail.event?.title ?? "—"} />
              <DetailRow label="Name"  value={detail.full_name} />
              <DetailRow label="Email" value={detail.email ?? "—"} />
              <DetailRow label="Phone" value={detail.phone ?? "—"} />
              <DetailRow label="Organization"     value={detail.organization ?? "—"} />
              <DetailRow label="Type of Business" value={detail.business_type ?? "—"} />
              <DetailRow label="Number of Guests" value={String(detail.guest_count ?? 1)} />
              <DetailRow label="Postal Code"      value={detail.postal_code ?? "—"} />
              <DetailRow label="Address"      value={detail.address ?? "—"} />
              <DetailRow label="Status"       value={detail.status} />
              <DetailRow label="Registered"   value={new Date(detail.registered_at).toLocaleString()} />
              {detail.notes && (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>
                    Notes
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}>
                    {detail.notes}
                  </Typography>
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={snack.severity} variant="filled" sx={{ borderRadius: 2 }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

const DetailRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Stack direction="row" gap={2}>
    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, minWidth: 100 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ flex: 1 }}>{value}</Typography>
  </Stack>
);

export default EventRegistrationsList;
